import * as React from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ErrorState, StatusPill } from '@/components/app';
import type { UserDetails } from '@/api/users';
import { USER_ROLE_LABELS, labelOf } from '@/lib/labels';
import { UserDetailSheetProps } from '../types';
import { daysLeft, formatDate, formatDateTime, userDisplayName, userInitials, userStatus } from '../format';
import { UserActivityTab } from './UserActivityTab';

const GOAL_LABELS: Record<string, string> = {
  first_exam: 'Первый экзамен',
  retake: 'Пересдача',
  repeat: 'Повтор',
};

const DEADLINE_LABELS: Record<string, string> = {
  '3_days': '3 дня',
  '1_week': '1 неделя',
  '1_month': '1 месяц',
};

/** Карточка пользователя — правая панель (макет UserDetail). */
export function UserDetailSheet({
  open,
  onClose,
  user,
  isLoading,
  error,
  onRetry,
  busy,
  onEdit,
  onGrantPremium,
  isSelf = false,
  onToggleBlock,
  onToggleAdmin,
  onDelete,
  onRestore,
}: UserDetailSheetProps) {
  const [tab, setTab] = React.useState<'overview' | 'activity'>('overview');
  const userId = user?.id;
  React.useEffect(() => setTab('overview'), [userId]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent aria-describedby={undefined}>
        <SheetHeader
          title="Карточка пользователя"
          closeLabel="Закрыть карточку"
          actions={
            user && !user.isDeleted ? (
              <Button variant="secondary" size="icon-sm" aria-label="Редактировать пользователя" onClick={() => onEdit(user)}>
                <Pencil />
              </Button>
            ) : undefined
          }
        />

        {error && !user ? (
          <SheetBody>
            <ErrorState message={error} onRetry={onRetry} />
          </SheetBody>
        ) : !user ? (
          <SheetBody aria-busy={isLoading}>
            <DetailSkeleton />
          </SheetBody>
        ) : (
          <>
            <SheetBody aria-busy={isLoading}>
              {error && <ErrorState compact message={error} onRetry={onRetry} />}
              <ProfileSection user={user} />
              <SubscriptionSection
                user={user}
                busy={busy}
                isSelf={isSelf}
                onGrantPremium={() => onGrantPremium(user)}
                onToggleBlock={() => onToggleBlock(user)}
                onToggleAdmin={() => onToggleAdmin(user)}
              />

              <Tabs value={tab} onValueChange={(v) => setTab(v as 'overview' | 'activity')}>
                <TabsList aria-label="Разделы карточки" className="w-full">
                  <TabsTrigger value="overview" className="flex-1">
                    Обзор
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="flex-1">
                    Активность
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="flex flex-col gap-3.5">
                  <OverviewTab user={user} />
                </TabsContent>
                <TabsContent value="activity">
                  <UserActivityTab userId={user.id} />
                </TabsContent>
              </Tabs>
            </SheetBody>

            <SheetFooter className="justify-between">
              <span className="min-w-0 flex-1 basis-56 text-xs text-text-2">
                {user.isDeleted
                  ? `Аккаунт удалён ${formatDateTime(user.deletedAt)}. Восстановление вернёт вход, прогресс и подписку.`
                  : 'Удаление мягкое: аккаунт помечается удалённым, данные сохраняются, его можно восстановить из списка с фильтром «Удалён».'}
              </span>
              {user.isDeleted ? (
                <Button variant="secondary" aria-haspopup="dialog" onClick={() => onRestore(user)}>
                  Восстановить аккаунт…
                </Button>
              ) : (
                <Button variant="destructiveOutline" aria-haspopup="dialog" onClick={() => onDelete(user)}>
                  Удалить аккаунт…
                </Button>
              )}
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ProfileSection({ user }: { user: UserDetails }) {
  const contact = user.email || 'нет email';
  return (
    <section aria-label="Профиль" className="flex gap-3.5 rounded-card border border-border-soft bg-surface p-4 shadow-card">
      <span
        aria-hidden
        className="grid size-12 shrink-0 place-items-center rounded-full bg-inverse font-display text-base font-bold text-on-inverse"
      >
        {userInitials(user)}
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <b className="truncate font-display text-lg font-bold">{userDisplayName(user)}</b>
        <span className="truncate text-sm text-text-2">
          {contact} · @{user.username}
        </span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <StatusPill size="sm" tone={user.role === 'admin' ? 'info' : 'neutral'}>
            {labelOf(USER_ROLE_LABELS, user.role)}
          </StatusPill>
          <StatusPill size="sm" tone={user.is_anonymous ? 'warn' : 'neutral'}>
            {user.is_anonymous ? 'Аноним' : 'Зарегистрирован'}
          </StatusPill>
          {user.googleId && <StatusPill size="sm">Google</StatusPill>}
          {user.appleId && <StatusPill size="sm">Apple</StatusPill>}
        </div>
        <span className="mt-1 text-xs text-text-2">
          Создан <span className="tabular-nums">{formatDateTime(user.created_at)}</span> · ID{' '}
          <span className="font-mono" title={user.id}>
            {user.id.slice(0, 8)}…{user.id.slice(-4)}
          </span>
        </span>
      </div>
    </section>
  );
}

interface SubscriptionSectionProps {
  user: UserDetails;
  busy: boolean;
  onGrantPremium: () => void;
  isSelf: boolean;
  onToggleBlock: () => void;
  onToggleAdmin: () => void;
}

function SubscriptionSection({ user, busy, isSelf, onGrantPremium, onToggleBlock, onToggleAdmin }: SubscriptionSectionProps) {
  const status = userStatus(user);
  const left = user.isSubscribed ? daysLeft(user.subscribedEnd) : null;
  const disabled = busy || user.isDeleted;

  return (
    <section aria-label="Подписка" className="flex flex-col gap-3 rounded-card border border-border-soft bg-surface p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[13px] text-text-2">Статус</span>
          <b className="font-display text-[17px] font-bold">
            {user.isSubscribed ? (
              user.subscribedEnd ? (
                <>
                  Premium до <span className="tabular-nums">{formatDate(user.subscribedEnd)}</span>
                </>
              ) : (
                'Premium'
              )
            ) : (
              'Free'
            )}
          </b>
          <span className="text-[13px] text-text-2">
            {user.isSubscribed
              ? left !== null
                ? `Осталось ${left} дн.`
                : 'Срок подписки истёк'
              : 'Без подписки'}
            {' · '}последний вход {formatDateTime(user.lastLogin)}
          </span>
        </div>
        <StatusPill tone={status.tone} dot>
          {status.label}
        </StatusPill>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" aria-haspopup="dialog" disabled={disabled} onClick={onGrantPremium}>
          Выдать Premium…
        </Button>
        {user.isBlocked ? (
          <Button variant="secondary" size="sm" aria-haspopup="dialog" disabled={disabled || isSelf} onClick={onToggleBlock}>
            Разблокировать…
          </Button>
        ) : (
          <Button variant="destructiveOutline" size="sm" aria-haspopup="dialog" disabled={disabled || isSelf} onClick={onToggleBlock}>
            Заблокировать…
          </Button>
        )}
        <Button variant="secondary" size="sm" aria-haspopup="dialog" disabled={disabled || isSelf} onClick={onToggleAdmin}>
          {user.role === 'admin' ? 'Снять роль админа…' : 'Сделать админом…'}
        </Button>
      </div>
      <span className="text-xs text-text-2">
        {isSelf
          ? 'Это ваш аккаунт: роль и блокировку может изменить только другой админ.'
          : 'Каждое действие откроет окно подтверждения и попадёт в аудит-лог.'}
      </span>
    </section>
  );
}

function OverviewTab({ user }: { user: UserDetails }) {
  return (
    <>
      {user.limits && (
        <div role="group" aria-label="Лимиты" className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
          <LimitTile label="Пробные экзамены" used={user.limits.mock_exam_attempts_used} remaining={user.limits.mock_exam_attempts_remaining} />
          <LimitTile label="Работа над ошибками" used={user.limits.wrong_exam_attempts_used} remaining={user.limits.wrong_exam_attempts_remaining} />
          <LimitTile label="Марафон" used={user.limits.marathon_attempts_used} remaining={user.limits.marathon_attempts_remaining} />
        </div>
      )}

      <InfoCard title="Основное">
        <InfoRow label="Логин">{user.username}</InfoRow>
        <InfoRow label="Email">{user.email || '—'}</InfoRow>
        <InfoRow label="Имя">{user.firstName || '—'}</InfoRow>
        <InfoRow label="Фамилия">{user.lastName || '—'}</InfoRow>
        <InfoRow label="Цель">{user.goal ? labelOf(GOAL_LABELS, user.goal) : '—'}</InfoRow>
        <InfoRow label="Срок подготовки">{user.deadline ? labelOf(DEADLINE_LABELS, user.deadline) : '—'}</InfoRow>
        <InfoRow label="Premium по лимитам">{user.limits ? (user.limits.isPremium ? 'Да' : 'Нет') : '—'}</InfoRow>
      </InfoCard>

      <InfoCard title="Устройство и вход">
        <InfoRow label="ID пользователя" mono>
          {user.id}
        </InfoRow>
        <InfoRow label="ID устройства" mono>
          {user.deviceId || '—'}
        </InfoRow>
        <InfoRow label="Google ID" mono>
          {user.googleId || '—'}
        </InfoRow>
        <InfoRow label="Apple ID" mono>
          {user.appleId || '—'}
        </InfoRow>
      </InfoCard>

      <InfoCard title="Даты">
        <InfoRow label="Создан">{formatDateTime(user.created_at)}</InfoRow>
        <InfoRow label="Обновлён">{formatDateTime(user.updated_at)}</InfoRow>
        <InfoRow label="Последний вход">{formatDateTime(user.lastLogin)}</InfoRow>
        {user.isDeleted && <InfoRow label="Удалён">{formatDateTime(user.deletedAt)}</InfoRow>}
      </InfoCard>
    </>
  );
}

function LimitTile({ label, used, remaining }: { label: string; used: number; remaining: number }) {
  return (
    <div className="flex flex-col gap-1 rounded-card border border-border-soft bg-surface px-4 py-3 shadow-card">
      <span className="text-[13px] text-text-2">{label}</span>
      <b className="font-display text-2xl font-extrabold tabular-nums">{used}</b>
      <span className="text-xs text-text-2">
        {remaining === -1 ? 'без лимита' : remaining >= 0 ? `осталось ${remaining}` : 'использовано'}
      </span>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className="rounded-card border border-border-soft bg-surface shadow-card">
      <b className="block px-4 pb-1 pt-3.5 font-display text-[15px] font-bold">{title}</b>
      <dl className="m-0 px-4 pb-2">{children}</dl>
    </section>
  );
}

function InfoRow({ label, mono, children }: { label: string; mono?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-border-soft py-2 first:border-t-0">
      <dt className="shrink-0 text-[13px] text-text-2">{label}</dt>
      <dd className={mono ? 'm-0 break-all text-right font-mono text-xs' : 'm-0 text-right text-sm'}>{children}</dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <>
      <div className="flex gap-3.5 rounded-card border border-border-soft bg-surface p-4">
        <Skeleton className="size-12 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <Skeleton className="h-36 w-full rounded-card" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-48 w-full rounded-card" />
    </>
  );
}
